import React, { useEffect, useState } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import QRCode from 'react-qr-code';
import DOMPurify from 'dompurify';
import * as storageService from '../services/storageService';
import { Patient, Room, Hospital, PatientStatus, BoardLayout, STATUS_CONFIG, SUB_STATUS_CONFIG, DEFAULT_AD_SETTINGS, isAssetActiveRightNow } from '../types';
import { HeartPulse, ChevronLeft, Sun, Moon, Clock, Stethoscope } from 'lucide-react';
import AdBanner from '../components/AdBanner';
import AdTicker from '../components/AdTicker';
import AdFullscreen from '../components/AdFullscreen';
import { ZenClock } from '../components/tv-modes/ZenClock';
import { MediaEmbed } from '../components/tv-modes/MediaEmbed';
import { GridLayout, SidebarAdLayout, FocusSingleLayout, TickerOnlyLayout } from '../components/tv-modes/BoardLayouts';

const PublicBoard: React.FC = () => {
    const { handle } = useParams<{ handle: string }>();
    const [hospital, setHospital] = useState<Hospital | null>(null);
    const [patients, setPatients] = useState<Patient[]>([]);
    const [rooms, setRooms] = useState<Room[]>([]);
    const [currentTime, setCurrentTime] = useState(new Date());
    const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
    const [currentPage, setCurrentPage] = useState(0);

    const activeBanners = hospital?.preferences?.promotionalBanners?.filter(b => b.isActive) || [];

    // Ad System
    const adSettings = hospital?.preferences?.adSettings || DEFAULT_AD_SETTINGS;
    const adAssets = [
        ...(hospital?.globalAdAssets || []),
        ...(hospital?.preferences?.adAssets || [])
    ];
    const adsEnabledTV = adSettings.adsEnabled && adSettings.showAdsOnTV;

    // Ad Injection State
    const [adsVisibleRound, setAdsVisibleRound] = useState(false);

    const [searchParams, setSearchParams] = useSearchParams();

    // Theme State
    const [theme, setTheme] = useState<'dark' | 'light'>(() => {
        // Preview overrides
        const themeOverride = searchParams.get('themeOverride');
        if (themeOverride === 'dark' || themeOverride === 'light') return themeOverride;

        // 1. Check URL first
        const urlTheme = searchParams.get('theme');
        if (urlTheme === 'dark' || urlTheme === 'light') {
            localStorage.setItem('tvTheme', urlTheme);
            return urlTheme;
        }
        // 2. Check local storage
        return (localStorage.getItem('tvTheme') as 'dark' | 'light') || 'dark';
    });
    const [currentScreenGroup, setCurrentScreenGroup] = useState<string | undefined>(undefined);
    const [screenMode, setScreenMode] = useState<'BOARD' | 'FULL_ADS' | 'CLOCK' | 'MEDIA_URL'>(() => {
        const modeOverride = searchParams.get('modeOverride');
        if (modeOverride === 'BOARD' || modeOverride === 'FULL_ADS' || modeOverride === 'CLOCK' || modeOverride === 'MEDIA_URL') {
            return modeOverride as any;
        }
        return 'BOARD';
    });
    const [screenModeData, setScreenModeData] = useState<string | null>(null);

    // Derived: if screenModeData carries a boardLayout prefix, extract it
    const boardLayout: BoardLayout = (() => {
        const layoutOverride = searchParams.get('layoutOverride');
        if (layoutOverride) return layoutOverride as BoardLayout;

        if (screenModeData?.startsWith('boardLayout:')) {
            const parsed = screenModeData.replace('boardLayout:', '') as BoardLayout;
            const valid: BoardLayout[] = ['LIST', 'GRID_2COL', 'SIDEBAR_AD', 'FOCUS_SINGLE', 'TICKER_ONLY'];
            if (valid.includes(parsed)) return parsed;
        }
        return 'LIST';
    })();

    // KIOSK / Pairing State
    const [needsPairing, setNeedsPairing] = useState(false);
    const [pairingCode, setPairingCode] = useState<string | null>(null);

    useEffect(() => {
        if (!searchParams.has('themeOverride')) {
            localStorage.setItem('tvTheme', theme);
        }
        // Clean URL if it has theme param
        if (searchParams.has('theme')) {
            const newParams = new URLSearchParams(searchParams);
            newParams.delete('theme');
            setSearchParams(newParams, { replace: true });
        }
    }, [theme, searchParams, setSearchParams]);

    // Wake Lock — prevent screen from sleeping on TV displays
    useEffect(() => {
        let wakeLock: WakeLockSentinel | null = null;

        const requestWakeLock = async () => {
            try {
                if ('wakeLock' in navigator) {
                    wakeLock = await (navigator as any).wakeLock.request('screen');
                    console.log('Wake Lock active');
                }
            } catch (e) { console.log('Wake Lock not available:', e); }
        };

        requestWakeLock();

        // Re-acquire wake lock when page becomes visible again
        const handleVisibility = () => {
            if (document.visibilityState === 'visible') requestWakeLock();
        };
        document.addEventListener('visibilitychange', handleVisibility);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibility);
            wakeLock?.release();
        };
    }, []);

    // TV Extreme Resilience: Anti-Memory Leak & Network Freeze Protection
    useEffect(() => {
        // 1. Force hard refresh if Wi-Fi drops and reconnects
        const handleOnline = () => {
            console.log("[TV Resilience] Red restaurada tras corte. Forzando Hard Refresh en 5s...");
            // Grace period in case the network needs seconds to stabilize routing
            setTimeout(() => window.location.reload(), 5000); 
        };
        window.addEventListener('online', handleOnline);

        // 2. Nightly Hard Refresh at 3:00 AM (Clears TV RAM and DOM constraints)
        const now = new Date();
        const next3AM = new Date(now);
        next3AM.setHours(3, 0, 0, 0);
        if (now.getHours() >= 3) {
            next3AM.setDate(next3AM.getDate() + 1);
        }
        const msUntil3AM = next3AM.getTime() - now.getTime();
        
        const nightTimer = setTimeout(() => {
            console.log("[TV Resilience] Iniciando Mantenimiento Nocturno (3:00 AM)...");
            window.location.reload();
        }, msUntil3AM);

        // 3. Fallback Hard Refresh every 12 hours max uptime
        const maxUptimeTimer = setTimeout(() => {
            console.log("[TV Resilience] Tiempo máximo de uptime (12h) alcanzado. Refrescando...");
            window.location.reload();
        }, 12 * 60 * 60 * 1000);

        return () => {
            window.removeEventListener('online', handleOnline);
            clearTimeout(nightTimer);
            clearTimeout(maxUptimeTimer);
        };
    }, []);

    // Screen Heartbeat — send periodic heartbeat for fleet monitoring
    useEffect(() => {
        if (!hospital || !handle) return;

        // 🔒 ZERO-TRUST REJECTION: Do NOT pollute screen fleet inside HQ if operated by Doctor checking the board via url
        const isLoggedStaff = storageService.isAuthenticated();
        const screenToken = localStorage.getItem('tv_screen_token');
        if (isLoggedStaff && !screenToken) {
            return;
        }

        // Generate or retrieve persistent screen ID
        const SCREEN_ID_KEY = 'hospital_status_screen_id';
        let screenId = searchParams.get('screenId');
        if (!screenId) {
            screenId = localStorage.getItem(SCREEN_ID_KEY);
            if (!screenId) {
                screenId = `SCR-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
                localStorage.setItem(SCREEN_ID_KEY, screenId);
            }
        }

        // Read screen name from URL param or use default
        const screenName = searchParams.get('screenName') || 'Pantalla sin nombre';

        const sendHeartbeat = async () => {
            try {
                const result = await storageService.sendScreenHeartbeat({
                    screenId: screenId!,
                    hospitalId: hospital.id,
                    screenName,
                    handle: handle!,
                    userAgent: navigator.userAgent,
                    resolution: `${window.screen.width}x${window.screen.height}`,
                    theme,
                });
                // Apply admin-forced theme if set
                if (result?.config?.forcedTheme && result.config.forcedTheme !== theme && !searchParams.has('themeOverride')) {
                    setTheme(result.config.forcedTheme as 'dark' | 'light');
                }
            } catch { /* silent fail */ }
        };

        // Send immediately on mount, then every 60 minutes
        sendHeartbeat();
        const interval = setInterval(sendHeartbeat, 3_600_000);

        return () => clearInterval(interval);
    }, [hospital, handle, theme, searchParams]);

    // Real-time config stream — applies admin-forced theme instantly
    useEffect(() => {
        if (!hospital) return;
        const SCREEN_ID_KEY = 'hospital_status_screen_id';
        const screenId = searchParams.get('screenId') || localStorage.getItem(SCREEN_ID_KEY);
        if (!screenId) return;

        const unsubscribe = storageService.subscribeToScreenConfig(screenId, (config) => {
            if (config.forcedTheme && !searchParams.has('themeOverride')) {
                setTheme(prev => config.forcedTheme !== prev ? config.forcedTheme as 'dark' | 'light' : prev);
            }
            if (config.screenGroup !== undefined) {
                setCurrentScreenGroup(prev => config.screenGroup !== prev ? config.screenGroup : prev);
            }
            if (config.screenMode !== undefined) {
                setScreenMode(prev => config.screenMode !== prev ? config.screenMode : prev);
            }
            if (config.screenModeData !== undefined) {
                setScreenModeData(prev => config.screenModeData !== prev ? config.screenModeData : prev);
            }
            if (config.forceRefreshTs) {
                const lastTs = localStorage.getItem('lastForceRefreshTs');
                if (lastTs && lastTs !== config.forceRefreshTs.toString()) {
                    localStorage.setItem('lastForceRefreshTs', config.forceRefreshTs.toString());
                    window.location.reload();
                } else if (!lastTs) {
                    localStorage.setItem('lastForceRefreshTs', config.forceRefreshTs.toString());
                }
            }
        });

        return unsubscribe;
    }, [hospital]);

    const t = React.useMemo(() => ({
        bgMain: theme === 'dark' ? 'bg-[#0B1121]' : 'bg-slate-50',
        headerBg: theme === 'dark' ? 'bg-black/40' : 'bg-white/70',
        textMain: theme === 'dark' ? 'text-white' : 'text-slate-900',
        textMuted: theme === 'dark' ? 'text-white/50' : 'text-slate-500',
        textSubtle: theme === 'dark' ? 'text-white/40' : 'text-slate-400',
        borderMain: theme === 'dark' ? 'border-white/10' : 'border-slate-200',
        borderLight: theme === 'dark' ? 'border-white/5' : 'border-slate-100',
        cardBg: theme === 'dark' ? 'bg-white/5' : 'bg-white',
        cardHover: theme === 'dark' ? 'hover:bg-white/10' : 'hover:bg-slate-50',
        pillBg: theme === 'dark' ? 'bg-white/10' : 'bg-slate-100',
        divider: theme === 'dark' ? 'divide-white/10' : 'divide-slate-200',
        bannerGradient: theme === 'dark' ? 'from-[#0f172a] via-[#1e293b] to-[#0f172a]' : 'from-slate-100 via-white to-slate-100',
        progressBg: theme === 'dark' ? 'bg-white/5' : 'bg-slate-200',
        progressSecondary: theme === 'dark' ? 'bg-white/30' : 'bg-white/60',
    }), [theme]);


    useEffect(() => {
        const timer = setInterval(() => setCurrentTime(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    // Banner Rotation Logic
    useEffect(() => {
        if (activeBanners.length === 0) return;

        const currentBanner = activeBanners[currentBannerIndex];
        // Default to showing for 15 seconds if not specified
        const displayTime = (currentBanner?.displayTimeSec || 15) * 1000;

        const timer = setTimeout(() => {
            setCurrentBannerIndex((prev) => (prev + 1) % activeBanners.length);
        }, displayTime);

        return () => clearTimeout(timer);
    }, [activeBanners, currentBannerIndex]);

    const currentScreenId = React.useMemo(() => {
        return searchParams.get('screenId') || localStorage.getItem('hospital_status_screen_id') || 'unknown';
    }, [searchParams]);

    // Active Ads Computation based on currentTime
    const activeAdBanners = React.useMemo(() => {
        return adAssets.filter(a => isAssetActiveRightNow(a, currentTime, currentScreenGroup, currentScreenId));
    }, [adAssets, currentTime, currentScreenGroup, currentScreenId]);

    // ── AD IMPRESSION TRACKING ──
    // Buffer impressions and flush every 30s as a batch
    const impressionBufferRef = React.useRef<any[]>([]);
    const lastTrackedAdsRef = React.useRef<Set<string>>(new Set());

    useEffect(() => {
        if (!hospital || !adsEnabledTV || activeAdBanners.length === 0) return;

        const screenId = searchParams.get('screenId') || localStorage.getItem('hospital_status_screen_id') || 'unknown';

        // Track new ads that just became visible with 1h Cooldown
        const currentAdIds = new Set(activeAdBanners.map(a => a.id));
        const storageKey = 'hs_ads_last_seen';
        const lastSeenObj = JSON.parse(localStorage.getItem(storageKey) || '{}');
        const now = Date.now();
        const COOLDOWN_MS = 1 * 60 * 60 * 1000; // 1 hour

        const newlyPushedAds = activeAdBanners.filter(a => !lastTrackedAdsRef.current.has(a.id));

        let changedStorage = false;
        for (const ad of newlyPushedAds) {
            const lastSeen = lastSeenObj[ad.id] || 0;
            if ((now - lastSeen) > COOLDOWN_MS) {
                impressionBufferRef.current.push({
                    hospitalId: hospital.id,
                    adId: ad.id,
                    adType: ad.type,
                    source: 'tv',
                    eventType: ad.type === 'FULLSCREEN_SLIDE' ? 'fullscreen_shown' : 'impression',
                    screenId,
                });
                lastSeenObj[ad.id] = now;
                changedStorage = true;
            }
        }

        if (changedStorage) {
            localStorage.setItem(storageKey, JSON.stringify(lastSeenObj));
        }

        lastTrackedAdsRef.current = currentAdIds;
    }, [activeAdBanners, hospital, adsEnabledTV]);

    // Flush buffer every 30 seconds
    useEffect(() => {
        const flushInterval = setInterval(() => {
            if (impressionBufferRef.current.length === 0) return;
            const events = [...impressionBufferRef.current];
            impressionBufferRef.current = [];

            fetch('/api/ads/track/batch', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ events }),
            }).catch(() => {}); // Fire and forget
        }, 30000);

        return () => clearInterval(flushInterval);
    }, []);

    // Dynamic Ad Injection Trigger
    useEffect(() => {
        if (!adsEnabledTV) return;

        // Si hay banners activos (por hora, rango o frecuencia), abrimos el contenedor
        if (activeAdBanners.some(a => a.type === 'BANNER_TEXT' || a.type === 'BANNER_IMAGE')) {
            setAdsVisibleRound(true);
        }
    }, [activeAdBanners.length, adsEnabledTV]);

    useEffect(() => {
        let unsubP: (() => void) | null = null;
        let unsubR: (() => void) | null = null;
        let unsubC: (() => void) | null = null;
        let pollInterval: any;

        const loadBoard = async () => {
            if (!handle) return;
            const h = await storageService.getHospitalByHandle(handle);
            if (h) {
                setHospital(h);

                // 🔒 ZERO-TRUST GATE: Restrict public access to the TV board. 
                // Only allow valid Doctors or officially paired TV hardware.
                const isLoggedStaff = storageService.isAuthenticated();
                const screenToken = localStorage.getItem('tv_screen_token');

                if (!isLoggedStaff && !screenToken) {
                    setNeedsPairing(true);
                    
                    // Request pairing PIN from Cloudflare
                    const res = await storageService.requestScreenPairing(h.id);
                    if (res.code) {
                        setPairingCode(res.code);
                        // Poll for status approval by Admin inside HQ
                        pollInterval = setInterval(async () => {
                            const status = await storageService.checkScreenPairingStatus(res.code!);
                            if (status.status === 'PAIRED' && status.screenToken) {
                                localStorage.setItem('tv_screen_token', status.screenToken);
                                localStorage.setItem('hospital_status_screen_id', status.screenId || '');
                                clearInterval(pollInterval);
                                setNeedsPairing(false);
                                window.location.reload();
                            }
                        }, 5000);
                    }
                    return; // HALT execution, DO NOT subscribe to SSE streams. Patient data is locked.
                }

                // Subscribe to SSE
                unsubP = storageService.subscribeToPatients(h.id, setPatients);
                unsubR = storageService.subscribeToRooms(h.id, setRooms);

                // Real-time config stream for immediate ad/hospital setting updates
                unsubC = storageService.subscribeToHospitalConfig(h.id, (updatedHospital) => {
                    setHospital(updatedHospital);
                });
            }
        };
        loadBoard();

        return () => {
            if (unsubP) unsubP();
            if (unsubR) unsubR();
            if (unsubC) unsubC();
            if (pollInterval) clearInterval(pollInterval);
        };
    }, [handle]);

    const activePatients = patients.filter(p => {
        // 1. If discharged (EXIT), keep for 60 minutes max
        if (p.currentStatus === PatientStatus.EXIT) {
            if (!p.dischargeTime) return false;
            const discharge = new Date(p.dischargeTime).getTime();
            const now = new Date().getTime();
            return (now - discharge) < (60 * 60 * 1000);
        }

        // 2. Filter out anomalies: if someone is stuck at 100% progress but not in RECOVERY/EXIT, 
        // they shouldn't just sit on the board forever. We assume if they hit 100% they are effectively done.
        // Actually, let's just show them if they are in an active status: WAITING, PREPARATION, ROOM_ENTRY, ANESTHESIA, SURGERY, RECOVERY
        const activeStatuses = [
            PatientStatus.WAITING,
            PatientStatus.PREPARATION,
            PatientStatus.ROOM_ENTRY,
            PatientStatus.ANESTHESIA,
            PatientStatus.SURGERY,
            PatientStatus.RECOVERY
        ];

        if (!activeStatuses.includes(p.currentStatus)) {
            return false;
        }

        // 3. For surgeries, normally we want to see them. If for some reason a surgery is marked 100%
        // we keep showing it until the status changes to Recovery/Exit. 
        // But the user complained about "EN SALA / PREPARACIÓN" stuck at 100%. We fixed that visual bug earlier.

        // 4. Ghost Patient Prevention:
        // If an active patient doesn't have a valid room assigned, they are orphaned (e.g., room was deleted).
        // They should not be displayed on the Public TV Board.
        if (p.currentStatus !== PatientStatus.RECOVERY && p.currentStatus !== PatientStatus.EXIT) {
            const hasValidRoom = rooms.some(r => r.id === p.room);
            if (!hasValidRoom) {
                return false;
            }
        }

        return true;
    }).sort((a, b) => {
        if (a.currentStatus === PatientStatus.EXIT && b.currentStatus !== PatientStatus.EXIT) return 1;
        if (a.currentStatus !== PatientStatus.EXIT && b.currentStatus === PatientStatus.EXIT) return -1;
        return new Date(b.admissionTime).getTime() - new Date(a.admissionTime).getTime();
    });

    // Adaptive pagination: fewer items when ad banner is visible to avoid overlap
    const hasBannerVisible = adsVisibleRound && adsEnabledTV && activeAdBanners.some(a => a.type === 'BANNER_TEXT' || a.type === 'BANNER_IMAGE');
    const hasTickerVisible = adsEnabledTV && adSettings.tickerEnabled && activeAdBanners.some(a => a.type === 'TICKER');
    const ITEMS_PER_PAGE = (hasBannerVisible || hasTickerVisible) ? 4 : 6;
    const totalPages = Math.ceil(activePatients.length / ITEMS_PER_PAGE);

    // Reset page to 0 when ITEMS_PER_PAGE changes (ads toggling)
    useEffect(() => {
        setCurrentPage(0);
    }, [ITEMS_PER_PAGE]);

    // Auto-pagination effect
    useEffect(() => {
        if (totalPages <= 1) {
            if (currentPage !== 0) setCurrentPage(0);
            return;
        }

        const timer = setInterval(() => {
            setCurrentPage(prev => (prev + 1) % totalPages);
        }, 15000); // 15 seconds per page

        return () => clearInterval(timer);
    }, [totalPages, currentPage]);

    if (needsPairing && pairingCode) {
        return (
            <div className="min-h-screen bg-black flex flex-col items-center justify-center font-outfit p-10 text-white relative overflow-hidden">
                <div className="absolute inset-0 z-0 bg-gradient-to-br from-slate-900 via-black to-black opacity-80" />
                <div className="absolute inset-0 z-0 blur-[120px] opacity-40 mix-blend-screen" style={{ background: `radial-gradient(circle at center, ${hospital?.branding?.primaryColor || '#5B65DC'}, transparent)` }} />
                
                <div className="z-10 flex flex-col items-center gap-10 text-center max-w-3xl">
                    {hospital?.branding?.logoDarkUrl ? (
                         <img src={hospital.branding.logoDarkUrl} alt="Logo" className="h-28 object-contain mb-2 drop-shadow-2xl" />
                    ) : (
                         <HeartPulse className="w-24 h-24 mb-4 opacity-80" style={{ color: hospital?.branding?.primaryColor || '#5B65DC' }} />
                    )}
                    <h1 className="text-5xl md:text-6xl font-light tracking-tight text-white/95">
                        Pantalla Restringida
                    </h1>
                    <p className="text-xl md:text-2xl text-slate-400 font-light max-w-2xl leading-relaxed">
                        Sistema Zero-Trust activado. Para habilitar este monitor introduce el siguiente PIN en el Panel de Administración de Publicidad de tu hospital.
                    </p>
                    
                    <div className="mt-8 mb-4">
                        <div className="text-8xl md:text-[9rem] font-bold tracking-[0.1em] px-16 py-12 rounded-[3.5rem] border border-white/10 bg-white/5 backdrop-blur-xl shadow-2xl" style={{ color: hospital?.branding?.primaryColor || '#5B65DC' }}>
                            {pairingCode.substring(0, 3)} {pairingCode.substring(3)}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!hospital) {
        return (
            <div className="min-h-screen bg-[#0B1121] flex flex-col p-6 md:p-10 gap-6">
                <div className="w-full h-24 md:h-32 bg-white/5 rounded-3xl animate-pulse" />
                <div className="flex-1 w-full flex flex-col gap-4">
                    {[1, 2, 3, 4, 5, 6].map(i => (
                        <div key={i} className="w-full flex-1 bg-white/5 rounded-2xl animate-pulse" />
                    ))}
                </div>
            </div>
        );
    }

    // Unified initials helper
    const getInitials = (name: string) => {
        if (!name || name === 'Paciente Anónimo') return '';
        return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 3);
    };

    const getStatusDisplay = (patient: Patient) => {
        if (patient.currentStatus === PatientStatus.SURGERY && patient.currentSubStatus) {
            return SUB_STATUS_CONFIG[patient.currentSubStatus]?.label || patient.currentSubStatus;
        }
        return STATUS_CONFIG[patient.currentStatus]?.label || patient.currentStatus;
    };

    const paginatedPatients = activePatients.slice(
        currentPage * ITEMS_PER_PAGE,
        (currentPage + 1) * ITEMS_PER_PAGE
    );

    const getStatusStyles = (status: PatientStatus, primaryColor: string) => {
        const isDark = theme === 'dark';

        switch (status) {
            case PatientStatus.WAITING:
                return {
                    bg: isDark ? 'rgba(96, 165, 250, 0.1)' : 'rgba(96, 165, 250, 0.15)',
                    border: isDark ? 'rgba(96, 165, 250, 0.3)' : 'rgba(96, 165, 250, 0.4)',
                    text: isDark ? '#60a5fa' : '#1d4ed8',
                    glow: isDark ? 'rgba(96, 165, 250, 0.2)' : 'transparent',
                    progressBg: isDark ? '#60a5fa' : '#2563eb'
                };
            case PatientStatus.PREPARATION:
                return {
                    bg: isDark ? 'rgba(56, 189, 248, 0.1)' : 'rgba(56, 189, 248, 0.15)',
                    border: isDark ? 'rgba(56, 189, 248, 0.3)' : 'rgba(56, 189, 248, 0.4)',
                    text: isDark ? '#38bdf8' : '#0369a1',
                    glow: isDark ? 'rgba(56, 189, 248, 0.3)' : 'transparent',
                    progressBg: isDark ? '#38bdf8' : '#0284c7'
                };
            case PatientStatus.ANESTHESIA:
                return {
                    bg: isDark ? 'rgba(251, 191, 36, 0.1)' : 'rgba(251, 191, 36, 0.15)',
                    border: isDark ? 'rgba(251, 191, 36, 0.3)' : 'rgba(251, 191, 36, 0.4)',
                    text: isDark ? '#fbbf24' : '#b45309',
                    glow: isDark ? 'rgba(251, 191, 36, 0.3)' : 'transparent',
                    progressBg: isDark ? '#fbbf24' : '#d97706'
                };
            case PatientStatus.SURGERY:
                return {
                    bg: primaryColor,
                    border: primaryColor,
                    text: '#ffffff',
                    glow: isDark ? primaryColor : 'transparent',
                    progressBg: primaryColor
                };
            case PatientStatus.RECOVERY:
                return {
                    bg: isDark ? 'rgba(52, 211, 153, 0.1)' : 'rgba(52, 211, 153, 0.15)',
                    border: isDark ? 'rgba(52, 211, 153, 0.3)' : 'rgba(52, 211, 153, 0.4)',
                    text: isDark ? '#34d399' : '#047857',
                    glow: isDark ? 'rgba(52, 211, 153, 0.3)' : 'transparent',
                    progressBg: isDark ? '#34d399' : '#059669'
                };
            case PatientStatus.EXIT:
                return {
                    bg: isDark ? 'rgba(148, 163, 184, 0.1)' : 'rgba(148, 163, 184, 0.15)',
                    border: isDark ? 'rgba(148, 163, 184, 0.3)' : 'rgba(148, 163, 184, 0.4)',
                    text: isDark ? '#94a3b8' : '#475569',
                    glow: 'transparent',
                    progressBg: isDark ? '#94a3b8' : '#64748b'
                };
            default:
                return {
                    bg: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0,0,0,0.05)',
                    border: isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0,0,0,0.1)',
                    text: isDark ? '#ffffff' : '#334155',
                    glow: 'transparent',
                    progressBg: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'
                };
        }
    };

    // 🔒 ZERO-TRUST / STAFF BYPASS:
    // If it's a doctor inspecting the board via their phone, forcibly ignore Admin 
    // display overrides (like Zen Clock or Full Ads) and ALWAYS show the medical board.
    const isLoggedStaff = storageService.isAuthenticated();
    const isPairedTV = !!localStorage.getItem('tv_screen_token');
    const isPreview = searchParams.get('preview') === 'true';
    const effectiveScreenMode = (isLoggedStaff && !isPairedTV && !isPreview) ? 'BOARD' : screenMode;

    if (effectiveScreenMode === 'FULL_ADS') {
        const activeFullScreenAds = adAssets.filter(a => isAssetActiveRightNow(a, currentTime, currentScreenGroup, currentScreenId));
        const tickerAssets = adAssets.filter(a =>
            a.type === 'TICKER_TEXT' &&
            a.target !== 'MOBILE_ONLY' &&
            isAssetActiveRightNow(a, currentTime, currentScreenGroup, currentScreenId)
        );
        return (
            <div className={`h-screen max-h-screen ${t.bgMain} overflow-hidden`}>
                <AdFullscreen 
                    assets={activeFullScreenAds}
                    adSettings={{...adSettings, tickerEnabled: true}}
                    tickerAssets={tickerAssets}
                    theme={theme}
                    hospitalName={hospital.name}
                    hospitalLogo={hospital.branding.logoUrl}
                    hospitalLogoDark={hospital.branding.logoDarkUrl}
                    primaryColor={hospital.branding.primaryColor}
                    hospitalId={hospital.id}
                />
            </div>
        );
    }

    if (effectiveScreenMode === 'CLOCK') {
        return (
            <div className={`h-screen max-h-screen ${t.bgMain} overflow-hidden`}>
                <ZenClock 
                    theme={theme}
                    primaryColor={hospital.branding.primaryColor || '#5B65DC'}
                    hospitalName={hospital.name}
                    logoUrl={theme === 'dark' ? hospital.branding.logoDarkUrl : hospital.branding.logoUrl}
                />
            </div>
        );
    }

    if (effectiveScreenMode === 'MEDIA_URL') {
        return (
            <div className={`h-screen max-h-screen ${t.bgMain} overflow-hidden`}>
                <MediaEmbed 
                    theme={theme}
                    url={screenModeData}
                />
            </div>
        );
    }

    return (
        <div className={`h-screen max-h-screen ${t.bgMain} ${t.textMain} font-sans overflow-hidden flex flex-col`}>
            {/* Header */}
            <header className={`px-6 py-4 md:px-10 md:py-6 flex flex-wrap md:flex-nowrap items-center justify-between gap-4 md:gap-6 border-b ${t.borderMain} ${t.headerBg} backdrop-blur-md relative overflow-hidden`}>
                {/* Dynamic Brand Glow */}
                <div
                    className="absolute top-0 left-0 w-full h-full opacity-20"
                    style={{ background: `linear-gradient(90deg, ${hospital.branding.primaryColor || '#5B65DC'}, transparent)` }}
                />

                {/* Logo & Info - Left */}
                <div className="flex flex-col relative z-10 flex-1 min-w-[200px] justify-start order-1 gap-1">
                    <div className="flex items-center gap-4 md:gap-5">
                        <Link to={storageService.isAuthenticated() ? '/staff/dashboard' : `/${handle}`} className={`${t.textSubtle} hover:${t.textMain} transition-colors hover:scale-110 transform duration-300 shrink-0`}>
                            <ChevronLeft className="w-8 h-8 md:w-10 md:h-10" />
                        </Link>

                        {hospital.branding.logoDarkUrl && theme === 'dark' && hospital.branding.logoDarkUrl.trim() !== '' && hospital.branding.logoDarkUrl !== 'null' ? (
                            <div className={`h-12 md:h-14 min-w-[3rem] w-auto max-w-[150px] md:max-w-[200px] flex items-center justify-center p-2 ${t.pillBg} backdrop-blur-sm rounded-xl border ${t.borderMain} shadow-lg shrink-0`} style={{ boxShadow: `0 0 20px ${hospital.branding.primaryColor || '#5B65DC'}40` }}>
                                <img
                                    src={hospital.branding.logoDarkUrl}
                                    alt="Logo"
                                    className="max-h-full max-w-full object-contain filter drop-shadow-lg"
                                    onError={(e) => {
                                        (e.target as HTMLImageElement).style.display = 'none';
                                        (e.target as HTMLImageElement).parentElement!.innerHTML = DOMPurify.sanitize(`<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-6 h-6 md:w-8 md:h-8 text-slate-400"><path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/><path d="M12 5 9.04 9.2a2 2 0 0 0-.28 1.41l.36 1.76a2 2 0 0 0 1.96 1.6h3.84a2 2 0 0 0 1.96-1.6l.36-1.76a2 2 0 0 0-.28-1.41L12 5Z"/></svg>`);
                                    }}
                                />
                            </div>
                        ) : hospital.branding.logoUrl && hospital.branding.logoUrl.trim() !== '' && hospital.branding.logoUrl !== 'null' ? (
                            <div className={`h-12 md:h-14 min-w-[3rem] w-auto max-w-[150px] md:max-w-[200px] flex items-center justify-center p-2 ${t.pillBg} backdrop-blur-sm rounded-xl border ${t.borderMain} shadow-lg shrink-0`} style={{ boxShadow: `0 0 20px ${hospital.branding.primaryColor || '#5B65DC'}40` }}>
                                <img
                                    src={hospital.branding.logoUrl}
                                    alt="Logo"
                                    className="max-h-full max-w-full object-contain filter drop-shadow-lg"
                                    onError={(e) => {
                                        (e.target as HTMLImageElement).style.display = 'none';
                                        (e.target as HTMLImageElement).parentElement!.innerHTML = DOMPurify.sanitize(`<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-6 h-6 md:w-8 md:h-8 text-slate-400"><path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/><path d="M12 5 9.04 9.2a2 2 0 0 0-.28 1.41l.36 1.76a2 2 0 0 0 1.96 1.6h3.84a2 2 0 0 0 1.96-1.6l.36-1.76a2 2 0 0 0-.28-1.41L12 5Z"/></svg>`);
                                    }}
                                />
                            </div>
                        ) : (
                            <div
                                className={`w-12 h-12 md:w-14 md:h-14 rounded-xl md:rounded-2xl flex items-center justify-center shadow-lg shrink-0`}
                                style={{
                                    backgroundColor: hospital.branding.primaryColor || '#5B65DC',
                                    boxShadow: `0 0 30px ${hospital.branding.primaryColor || '#5B65DC'}80`
                                }}
                            >
                                <HeartPulse className="w-6 h-6 md:w-8 md:h-8 text-white" />
                            </div>
                        )}


                    </div>
                    {/* Hospital name below logo — always visible */}
                    <div className="pl-12 md:pl-14">
                        <h1 className={`font-bold font-heading tracking-tight leading-tight`} style={{ fontSize: 'clamp(0.85rem, 1.2vw, 1.5rem)', color: hospital.branding.primaryColor || (theme === 'dark' ? '#ffffff' : '#0f172a'), textShadow: theme === 'dark' ? `0 0 20px ${hospital.branding.primaryColor || '#5B65DC'}40` : 'none' }}>
                            {hospital.name}
                        </h1>
                    </div>
                </div>

                {/* Clock & Status - Center */}
                <div className={`text-center flex flex-col items-center justify-center relative z-10 w-full md:w-auto shrink-0 order-3 md:order-2 py-2 md:py-0`}>
                    <div className={`font-black font-mono tracking-tighter ${t.textMain} drop-shadow-md flex items-center justify-center gap-4`} style={{ fontSize: 'clamp(2.5rem, 4vw, 4rem)' }}>
                        <button onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')} className={`p-2 rounded-xl transition-all ${theme === 'dark' ? 'bg-white/5 text-slate-400 hover:text-white' : 'bg-slate-200/50 text-slate-500 hover:text-slate-800'} hidden md:block`}>
                            {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                        </button>
                        {currentTime.toLocaleTimeString('es-MX', { hour12: false, hour: '2-digit', minute: '2-digit' })}
                    </div>
                    <span
                        className="text-xs font-black uppercase tracking-[0.2em] mt-2 flex items-center justify-center gap-2"
                        style={{ color: hospital.branding.primaryColor || '#5B65DC' }}
                    >
                        <span className="w-2.5 h-2.5 rounded-full animate-ping" style={{ backgroundColor: hospital.branding.primaryColor || '#5B65DC' }}></span>
                        EN VIVO
                    </span>

                </div>

                {/* QR Code - Right */}
                <div className={`flex flex-col items-end justify-center flex-1 min-w-[160px] relative z-10 order-2 md:order-3`}>
                    <div className={`hidden md:flex items-center gap-3 ${t.cardBg} p-2 rounded-2xl border ${t.borderLight} backdrop-blur-md shadow-lg transition-transform hover:scale-[1.02]`}>
                        <div className="flex flex-col text-right justify-center pl-2">
                            <span className={`text-[10px] font-bold uppercase tracking-[0.2em] ${t.textSubtle}`}>Acceso Familiar</span>
                            <span className={`text-sm font-black tracking-widest ${t.textMain} leading-tight mb-1`}>ESCANEA AQUÍ</span>
                            <span className={`text-[9px] font-medium ${t.textMuted}`}>Sigue el estatus en tu celular</span>
                        </div>
                        <div className="p-1.5 bg-white rounded-xl shadow-sm">
                            <QRCode value={`${window.location.origin}/family?h=${hospital.handle}`} size={64} fgColor="#000000" bgColor="#FFFFFF" level="L" />
                        </div>
                    </div>
                </div>
            </header>

            {/* Board */}
            <main className="flex-1 px-4 py-2 md:px-6 md:py-3 overflow-hidden flex flex-col min-h-0">
                <div className={`${t.cardBg} rounded-2xl md:rounded-3xl overflow-hidden flex-1 flex flex-col border ${t.borderMain} shadow-2xl backdrop-blur-sm min-h-0`}>
                    {/* Column headers — only for LIST and SIDEBAR_AD layouts */}
                    {(boardLayout === 'LIST' || boardLayout === 'SIDEBAR_AD') && (
                    <div className={`hidden md:grid grid-cols-12 gap-4 px-8 py-4 ${t.cardBg} border-b ${t.borderMain} font-bold ${t.textSubtle} uppercase tracking-widest`} style={{ fontSize: 'clamp(0.6rem, 0.7vw, 0.85rem)' }}>
                        <div className="col-span-3">Paciente / Procedimiento</div>
                        <div className="col-span-2">Doctor</div>
                        <div className="col-span-2">Quirófano</div>
                        <div className="col-span-2 text-center">Progreso</div>
                        <div className="col-span-3 text-right flex justify-end items-center gap-3">
                            <span>Estatus Actual</span>
                            {totalPages > 1 && (
                                <span className={`px-2 py-0.5 rounded-full ${t.pillBg} text-xs font-black`} style={{ color: hospital.branding.primaryColor || '#5B65DC' }}>
                                    Pág. {currentPage + 1} de {totalPages}
                                </span>
                            )}
                        </div>
                    </div>
                    )}

                    <div className="flex-1 overflow-hidden min-h-0">
                        {/* ── GRID_2COL Layout ────────────────────────── */}
                        {boardLayout === 'GRID_2COL' && (
                            <GridLayout
                                patients={activePatients}
                                rooms={rooms}
                                theme={theme}
                                t={t as any}
                                primaryColor={hospital.branding.primaryColor || '#5B65DC'}
                                getStatusStyles={getStatusStyles}
                                getStatusDisplay={getStatusDisplay}
                                adAssets={adAssets}
                                adSettings={adSettings}
                                adsEnabled={adsEnabledTV}
                                hospitalId={hospital.id}
                            />
                        )}

                        {/* ── SIDEBAR_AD Layout ───────────────────────── */}
                        {boardLayout === 'SIDEBAR_AD' && (
                            <SidebarAdLayout
                                patients={activePatients}
                                rooms={rooms}
                                theme={theme}
                                t={t as any}
                                primaryColor={hospital.branding.primaryColor || '#5B65DC'}
                                getStatusStyles={getStatusStyles}
                                getStatusDisplay={getStatusDisplay}
                                adAssets={adAssets}
                                adSettings={adSettings}
                                adsEnabled={adsEnabledTV}
                                hospitalId={hospital.id}
                            />
                        )}

                        {/* ── FOCUS_SINGLE Layout ─────────────────────── */}
                        {boardLayout === 'FOCUS_SINGLE' && (
                            <FocusSingleLayout
                                patients={activePatients}
                                rooms={rooms}
                                theme={theme}
                                t={t as any}
                                primaryColor={hospital.branding.primaryColor || '#5B65DC'}
                                getStatusStyles={getStatusStyles}
                                getStatusDisplay={getStatusDisplay}
                                adAssets={adAssets}
                                adSettings={adSettings}
                                adsEnabled={adsEnabledTV}
                                hospitalId={hospital.id}
                            />
                        )}

                        {/* ── TICKER_ONLY Layout ──────────────────────── */}
                        {boardLayout === 'TICKER_ONLY' && (
                            <TickerOnlyLayout
                                patients={activePatients}
                                rooms={rooms}
                                theme={theme}
                                t={t as any}
                                primaryColor={hospital.branding.primaryColor || '#5B65DC'}
                                getStatusStyles={getStatusStyles}
                                getStatusDisplay={getStatusDisplay}
                                adAssets={adAssets}
                                adSettings={adSettings}
                                adsEnabled={adsEnabledTV}
                                hospitalId={hospital.id}
                                hospitalName={hospital.name}
                                logoUrl={hospital.branding.logoUrl || undefined}
                                logoDarkUrl={hospital.branding.logoDarkUrl || undefined}
                            />
                        )}

                        {/* ── LIST Layout (default) ───────────────────── */}
                        {boardLayout === 'LIST' && (
                        activePatients.length === 0 ? (
                            adsEnabledTV && adSettings.fullscreenWhenEmpty && activeAdBanners.some(a => a.type === 'FULLSCREEN_SLIDE') ? (
                                <AdFullscreen
                                    assets={activeAdBanners}
                                    adSettings={adSettings}
                                    hospitalName={hospital.name}
                                    hospitalLogo={hospital.branding.logoUrl || undefined}
                                    hospitalLogoDark={hospital.branding.logoDarkUrl || undefined}
                                    primaryColor={hospital.branding.primaryColor || '#5B65DC'}
                                    theme={theme}
                                    tickerAssets={activeAdBanners}
                                    hospitalId={hospital.id}
                                />
                            ) : (
                                <div className={`h-full flex flex-col items-center justify-center ${t.textSubtle} animate-fade-in`}>
                                    <HeartPulse className="w-20 h-20 mb-6 opacity-20 animate-pulse" />
                                    <p className="text-2xl font-bold font-heading tracking-tight">Sin procedimientos activos</p>
                                </div>
                            )
                        ) : (
                            <div className={`divide-y ${t.divider} flex-col flex h-full min-h-0 justify-start transition-all duration-700 ease-in-out`}>
                                {paginatedPatients.map(patient => {
                                    const room = rooms.find(r => r.id === patient.room);
                                    const primaryColor = hospital.branding.primaryColor || '#5B65DC';
                                    const statusStyles = getStatusStyles(patient.currentStatus, primaryColor);
                                    const isSurgery = patient.currentStatus === PatientStatus.SURGERY;

                                    return (
                                        <div key={patient.id} className={`flex flex-col md:grid md:grid-cols-12 gap-4 px-6 py-6 md:px-8 md:py-6 items-start md:items-center ${t.cardHover} transition-all duration-500 ease-in-out group animate-fade-in relative border-b ${t.borderLight} last:border-0 shrink-0`} style={{ animationDuration: '0.5s', borderLeft: patient.isDoctorEnCamino ? '4px solid #fbbf24' : '4px solid transparent' }}>
                                            {!!patient.isDoctorEnCamino && (
                                                <div className="absolute left-0 top-0 bottom-0 w-24 pointer-events-none" style={{ background: 'linear-gradient(90deg, rgba(251,191,36,0.12), transparent)' }}></div>
                                            )}
                                            <div className="flex flex-col w-full md:col-span-3 justify-center gap-1">
                                                <div className="flex justify-between items-center w-full gap-2">
                                                    <div className={`font-bold font-heading tracking-tight ${t.textMain} ${theme === 'dark' ? 'group-hover:drop-shadow-[0_0_8px_rgba(255,255,255,0.5)]' : ''} transition-all flex items-center gap-2`} style={{ fontSize: 'clamp(1.25rem, 2.2vw, 2.25rem)' }}>
                                                        {patient.id}
                                                        {!!patient.isDoctorEnCamino && (
                                                            <span className="hidden md:inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-400/15 border border-amber-400/30" style={{ fontSize: 'clamp(0.5rem, 0.6vw, 0.7rem)' }}>
                                                                <Stethoscope className="w-3 h-3 text-amber-500 animate-pulse" />
                                                                <span className="font-bold uppercase tracking-widest text-amber-500">Dr. Informará</span>
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="md:hidden flex flex-col items-end gap-2">
                                                        <span className="px-4 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider border"
                                                            style={{ backgroundColor: statusStyles.bg, borderColor: statusStyles.border, color: statusStyles.text }}>
                                                            {getStatusDisplay(patient)}
                                                        </span>
                                                        {!!patient.isDoctorEnCamino && (
                                                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-amber-400/20 text-amber-500 border border-amber-400/30 animate-pulse">
                                                                <Stethoscope className="w-3 h-3" />Médico Informará
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className={`${t.textMuted} font-medium truncate pr-4`} style={{ fontSize: 'clamp(0.75rem, 1vw, 1.1rem)' }}>
                                                    {patient.procedureName || 'Procedimiento'}
                                                </div>
                                                {patient.doctorName && (
                                                    <div className={`text-xs ${t.textSubtle} font-medium truncate pr-4 md:hidden`}>{patient.doctorName}</div>
                                                )}
                                            </div>
                                            <div className="hidden md:flex md:col-span-2 items-center">
                                                <span className={`font-medium ${t.textMuted} truncate`} style={{ fontSize: 'clamp(0.75rem, 1vw, 1.1rem)' }}>{patient.doctorName || '---'}</span>
                                            </div>
                                            {(() => {
                                                const isPostSurgery = patient.currentStatus === PatientStatus.RECOVERY || patient.currentStatus === PatientStatus.EXIT;
                                                const isPreSurgery = [PatientStatus.WAITING, PatientStatus.PREPARATION, PatientStatus.ROOM_ENTRY, PatientStatus.ANESTHESIA].includes(patient.currentStatus);
                                                const displayProgress = isPostSurgery ? 100 : (isPreSurgery ? 0 : patient.surgeryProgress);
                                                return (
                                                    <div className="flex md:contents w-full justify-between items-center gap-4 relative z-10">
                                                        <div className="w-1/2 md:w-auto md:col-span-2">
                                                            <span className={`inline-flex items-center px-3 py-1 md:px-4 md:py-1.5 rounded-lg ${t.cardBg} ${t.textMain} font-mono font-bold border ${t.borderMain} ${t.cardHover} transition-colors`} style={{ fontSize: 'clamp(0.85rem, 1.2vw, 1.25rem)' }}>
                                                                {isPostSurgery ? 'Recuperación' : (room?.name || '---')}
                                                            </span>
                                                        </div>
                                                        <div className="w-1/2 md:w-auto md:col-span-2">
                                                            <div className="flex items-center gap-2">
                                                                <div className={`flex-1 h-2 md:h-3 rounded-full ${t.progressBg} overflow-hidden ring-1 ${theme === 'dark' ? 'ring-white/10' : 'ring-slate-200'}`}>
                                                                    <div className="h-full transition-all duration-1000 relative" style={{ width: `${displayProgress}%`, backgroundColor: statusStyles.progressBg }}>
                                                                        {isSurgery && <div className={`absolute inset-0 ${t.progressSecondary} animate-pulse`}></div>}
                                                                    </div>
                                                                </div>
                                                                <span className={`font-mono font-medium opacity-35 ${t.textMuted} whitespace-nowrap`} style={{ fontSize: 'clamp(0.55rem, 0.7vw, 0.75rem)' }}>{displayProgress}%</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })()}
                                            <div className="hidden md:block col-span-3 text-right">
                                                <div className="inline-block relative">
                                                    {statusStyles.glow !== 'transparent' && (
                                                        <div className="absolute inset-0 blur-xl opacity-40 rounded-full animate-pulse-glow" style={{ backgroundColor: statusStyles.glow }}></div>
                                                    )}
                                                    <span className="relative px-6 py-2.5 rounded-full font-bold uppercase tracking-widest shadow-lg border font-heading block backdrop-blur-sm"
                                                        style={{ fontSize: 'clamp(0.7rem, 0.9vw, 1rem)', backgroundColor: statusStyles.bg, borderColor: statusStyles.border, color: statusStyles.text }}>
                                                        {getStatusDisplay(patient)}
                                                    </span>
                                                    {(patient.currentCondition === 'DELAYED' || patient.currentCondition === 'EXTENDED') && patient.currentStatus === PatientStatus.SURGERY && (
                                                        <div className={`absolute -top-2.5 -right-2.5 rounded-full p-1 border shadow-md flex items-center justify-center transition-all ${theme === 'dark' ? 'bg-slate-800 border-slate-700/50' : 'bg-white border-slate-100'}`}>
                                                            <Clock className={`w-4 h-4 opacity-80 ${patient.currentCondition === 'DELAYED' ? 'text-amber-500' : 'text-rose-500'}`} />
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                                {totalPages > 1 && (
                                    <div className={`mt-auto flex justify-center items-center gap-2 py-4 ${t.cardBg} border-t ${t.borderMain} w-full animate-fade-in shrink-0`}>
                                        {Array.from({ length: totalPages }).map((_, i) => (
                                            <div key={i} className={`h-2 rounded-full transition-all duration-500 ${i === currentPage ? 'w-8' : 'w-2 opacity-30'}`}
                                                style={{ backgroundColor: i === currentPage ? (hospital.branding.primaryColor || (theme === 'dark' ? '#ffffff' : '#64748b')) : (theme === 'dark' ? '#ffffff' : '#64748b') }} />
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Bottom Section: New Ad System + Legacy Banners */}
                <div className={`transition-all duration-700 ease-in-out origin-bottom ${adsVisibleRound ? 'opacity-100' : 'opacity-0 h-0 overflow-hidden'}`}>
                    {adsEnabledTV && activeAdBanners.some(a => a.type === 'BANNER_TEXT' || a.type === 'BANNER_IMAGE') ? (
                        <div className="mt-2 md:mt-3 shrink-0 relative z-20 space-y-2">
                            <AdBanner
                                assets={activeAdBanners}
                                variant="tv"
                                theme={theme}
                                primaryColor={hospital.branding.primaryColor || '#5B65DC'}
                                onCycleComplete={() => setAdsVisibleRound(false)}
                            />
                        </div>
                    ) : activeBanners.length > 0 && (
                        <div className="mt-2 md:mt-3 shrink-0 relative z-20">
                            {/* Legacy Promotional Banner (Digital Signage) */}
                            <div className={`w-full flex items-center justify-center bg-gradient-to-r ${t.bannerGradient} rounded-3xl border ${t.borderMain} p-4 relative overflow-hidden shadow-2xl`}>
                                <div className="flex items-center gap-4 relative z-10 max-w-5xl w-full animate-fade-in" key={currentBannerIndex}>
                                    {activeBanners[currentBannerIndex].icon && (
                                        <div className={`w-12 h-12 md:w-16 md:h-16 rounded-xl ${t.cardBg} flex items-center justify-center flex-shrink-0 border ${t.borderMain} shadow-inner`}>
                                            {activeBanners[currentBannerIndex].icon?.startsWith('http') ? (
                                                <img src={activeBanners[currentBannerIndex].icon} alt="Icon" className="w-8 h-8 md:w-10 md:h-10 object-contain" />
                                            ) : (
                                                <span className="text-2xl md:text-3xl">{activeBanners[currentBannerIndex].icon}</span>
                                            )}
                                        </div>
                                    )}
                                    <p className={`text-sm md:text-2xl font-medium tracking-wide flex-1 truncate ${t.textMain}`} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(activeBanners[currentBannerIndex].text) }} />
                                </div>
                                <div className={`absolute top-0 left-[-100%] w-1/2 h-full bg-gradient-to-r from-transparent ${theme === 'dark' ? 'via-white/5' : 'via-white/60'} to-transparent animate-[shimmer_8s_infinite] transition-all duration-1000`}></div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Ad Ticker */}
                {adsEnabledTV && adSettings.tickerEnabled && activeAdBanners.some(a => a.type === 'TICKER') && (
                    <div className="mt-1 shrink-0">
                        <AdTicker
                            assets={activeAdBanners}
                            speed={adSettings.tickerSpeed}
                            variant="tv"
                            theme={theme}
                            primaryColor={hospital.branding.primaryColor || '#5B65DC'}
                            hospitalId={hospital.id}
                        />
                    </div>
                )}
            </main>

            {/* Powered by footer */}
            <div className={`text-center py-1 ${t.textSubtle} shrink-0 opacity-30`} style={{ fontSize: 'clamp(0.40rem, 0.45vw, 0.5rem)' }}>
                powered by <span className="font-medium">hospital-status.com</span>
            </div>
        </div>
    );
};

export default PublicBoard;
